import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';

interface TimeEntry {
  id: number;
  date: string;
  hours: number;
  userId: number;
  userName: string;
  isDayRateDay: boolean;
  created_at: string;
  description: string;
  endTime: string;
  projectId: number;
  projectName: string;
  startTime: string;
  breakDuration: number;
}

interface UserHours {
  userId: number;
  userName: string;
  hours: number;
}

interface ProjectReport {
  id: number;
  name: string;
  client: string | null;
  totalHours: number;
  hoursToBill: number;
  dayRates: number;
  userHours: UserHours[];
  time_entries?: TimeEntry[];
}

const getMonthLabel = (selectedMonth: string): string => {
  const [year, month] = selectedMonth.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleString('de-DE', { month: 'long', year: 'numeric' });
};

export const exportProjectToPDF = async (project: ProjectReport, selectedMonth: string): Promise<void> => {
  try {
    const doc = new jsPDF();
    autoTable(doc, {}); // Initialisiere autoTable

    // Seiteneinstellungen
    const margin = 10;

    // Logo laden und als Base64 konvertieren
    try {
      const response = await fetch(process.env.PUBLIC_URL + '/logo.jpg');
      if (!response.ok) {
        throw new Error('Logo konnte nicht geladen werden');
      }
      
      const blob = await response.blob();
      
      // Promise für FileReader erstellen
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Lade das Bild und berechne das Seitenverhältnis
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = base64Data;
      });

      // Berechne die Höhe basierend auf dem Seitenverhältnis
      const imgWidth = 50; // Logo-Breite in mm
      const aspectRatio = img.height / img.width;
      const imgHeight = imgWidth * aspectRatio;

      // Logo hinzufügen
      doc.addImage(base64Data, 'JPEG', margin, 10, imgWidth, imgHeight);
      
    } catch (error) {
      console.warn('Logo konnte nicht geladen werden:', error);
    }

    // Titel
    doc.setFontSize(16);
    doc.text(`Projektbericht: ${project.name}`, margin, 40);

    // Projektinformationen
    doc.setFontSize(11);
    doc.text(`Kunde: ${project.client || '-'}`, margin, 55);
    doc.text(`Zeitraum: ${getMonthLabel(selectedMonth)}`, margin, 62);
    doc.text(`Gesamtstunden: ${project.totalHours.toFixed(2)}`, margin, 69);
    doc.text(`Stunden zum Abrechnen: ${project.hoursToBill.toFixed(2)}`, margin, 76);
    doc.text(`Tagessätze: ${project.dayRates}`, margin, 83);

    // Erstellungsdatum
    doc.setFontSize(9);
    doc.text(`Erstellt am: ${new Date().toLocaleString('de-DE')}`, margin, 95);

    // Tabellendaten vorbereiten
    const tableData = project.time_entries?.map(entry => [
      new Date(entry.date).toLocaleDateString('de-DE'),
      entry.userName,
      entry.startTime || '-',
      entry.endTime || '-',
      entry.breakDuration !== undefined ? entry.breakDuration.toString() : '-',
      entry.hours.toFixed(1),
      entry.isDayRateDay ? 'Tagessatz' : 'Stunden',
      entry.description || '-'
    ]) || [];

    // Tabelle erstellen
    autoTable(doc, {
      startY: 100,
      head: [['Datum', 'Mitarbeiter', 'Start', 'Ende', 'Pause\n(Min)', 'Stunden', 'Typ', 'Beschreibung']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'hidden', // Standard: kein Zeilenumbruch
        halign: 'left',
        valign: 'middle',
        lineWidth: 0.3
      },
      columnStyles: {
        0: { cellWidth: 24, overflow: 'hidden' }, // Datum - verbreitert für deutsches Datum
        1: { cellWidth: 35, overflow: 'hidden' }, // Mitarbeiter - genug Platz für Namen
        2: { cellWidth: 16, overflow: 'hidden' }, // Start - deutlich schmaler für "Start"
        3: { cellWidth: 16, overflow: 'hidden' }, // Ende - deutlich schmaler für "Ende"
        4: { cellWidth: 15, overflow: 'hidden' }, // Pause - feste Mindestbreite
        5: { cellWidth: 22, overflow: 'hidden' }, // Stunden - vergrößert für Überschrift
        6: { cellWidth: 22, overflow: 'hidden' }, // Typ - genug für "Tagessatz"
        7: { overflow: 'linebreak' }  // Beschreibung - bekommt mehr restliche Breite mit Zeilenumbruch
      },
      headStyles: {
        fillColor: [255, 224, 102], // #FFE066
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        overflow: 'linebreak' // Ermöglicht Zeilenumbruch in Überschriften
      }
    });

    // PDF speichern
    doc.save(`Projektbericht_${project.name}_${selectedMonth}.pdf`);
    
  } catch (err) {
    console.error('Fehler beim PDF-Export:', err);
    throw new Error('Beim Exportieren des PDF ist ein Fehler aufgetreten.');
  }
}; 